import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { inject, Injectable, NgZone } from '@angular/core';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { OrgHat, Organization, PendingPositionApproval } from '@newgenus/common';
import { ToastrService } from 'ngx-toastr';
import { Timestamp, arrayUnion, arrayRemove } from 'firebase/firestore';

/**
 * Service for managing hat-user assignments on the organization board.
 * Handles assigning/removing users to/from hats, and finding hats for positions.
 */
@Injectable({
    providedIn: 'root',
})
export class OrgHattingService {
    private db = inject(AngularFirestore);
    private auth = inject(AngularFireAuth);
    private toastr = inject(ToastrService);
    private ngZone = inject(NgZone);

    /**
     * Assigns a user to a hat by adding their uid to the hat's userKeys array.
     * This triggers the writeHat backend trigger which creates a security task.
     *
     * @param orgId The organization ID
     * @param hatId The hat key/ID to assign the user to
     * @param userId The user's uid to assign
     * @returns Promise with success status and optional error
     */
    public async assignUserToHat(
        orgId: string,
        hatId: string,
        userId: string
    ): Promise<{ succeeded: boolean; error?: string }> {
        try {
            const user = await this.auth.currentUser;
            if (!user) {
                return { succeeded: false, error: 'User not authenticated' };
            }

            // Update the hat document - add userId to assignedToArray
            // Using arrayUnion to prevent duplicates
            // Note: Backend processHats watches assignedToArray for user changes
            await this.db
                .collection('organizations')
                .doc(orgId)
                .collection('hats')
                .doc(hatId)
                .update({
                    assignedToArray: arrayUnion(userId),
                    userKeys: arrayUnion(userId),
                    modifiedBy: user.uid,
                    modifiedOn: Timestamp.now(),
                });

            // Sync denormalized org doc cache
            await this.db
                .collection('organizations')
                .doc(orgId)
                .update({
                    [`orgHats.${hatId}.assignedToArray`]: arrayUnion(userId),
                    [`orgHats.${hatId}.userKeys`]: arrayUnion(userId),
                });

            this.ngZone.run(() =>
                this.toastr.success('Staff member assignment initiated. Security task created.', 'Hat Assignment')
            );

            return { succeeded: true };
        } catch (error) {
            console.error('[OrgHattingService] Error assigning user to hat:', error);
            this.ngZone.run(() => this.toastr.error('Failed to assign staff member to hat.', 'Error'));
            return { succeeded: false, error: (error as Error).message };
        }
    }

    /**
     * Removes a user from a hat by removing their uid from the hat's userKeys array.
     * This triggers the writeHat backend trigger which creates a security task.
     *
     * @param orgId The organization ID
     * @param hatId The hat key/ID to remove the user from
     * @param userId The user's uid to remove
     * @returns Promise with success status and optional error
     */
    public async removeUserFromHat(
        orgId: string,
        hatId: string,
        userId: string
    ): Promise<{ succeeded: boolean; error?: string }> {
        try {
            const user = await this.auth.currentUser;
            if (!user) {
                return { succeeded: false, error: 'User not authenticated' };
            }

            // Update the hat document - remove userId from assignedToArray
            // Note: Backend processHats watches assignedToArray for user changes
            const hatDocRef = this.db.collection('organizations').doc(orgId).collection('hats').doc(hatId);

            // Read current hat to determine if this is the last user
            const hatSnap = await hatDocRef.ref.get();
            const hatData = hatSnap.data() as any;
            const currentAssigned = hatData?.assignedToArray || [];
            const willBeEmpty = currentAssigned.length <= 1 && currentAssigned.includes(userId);

            const updateData: Record<string, any> = {
                assignedToArray: arrayRemove(userId),
                userKeys: arrayRemove(userId),
                pendingAssignedToArray: arrayRemove(userId),
                modifiedBy: user.uid,
                modifiedOn: Timestamp.now(),
            };

            // Note: legacy `assigned`/`assignedTo` fields no longer written (TD#2, April 2026).
            // `assignedToArray` is the canonical multi-user truth source.

            await hatDocRef.update(updateData);

            // Sync denormalized org doc cache
            const orgRef = this.db.collection('organizations').doc(orgId);
            const orgCacheUpdate: Record<string, any> = {
                [`orgHats.${hatId}.assignedToArray`]: arrayRemove(userId),
                [`orgHats.${hatId}.userKeys`]: arrayRemove(userId),
                [`orgHats.${hatId}.pendingAssignedToArray`]: arrayRemove(userId),
            };
            await orgRef.update(orgCacheUpdate);

            // Note: Toaster notification is handled by the calling component to avoid duplicates
            // The component tracks task completion and shows appropriate messages

            // Best-effort: clean up any orphaned pendingApprovals records for this user+hat
            await this.cleanupPendingApprovalRecords(orgId, hatId, userId, 'rejected', 'Removed from hat');

            return { succeeded: true };
        } catch (error) {
            console.error('[OrgHattingService] Error removing user from hat:', error);
            this.ngZone.run(() => this.toastr.error('Failed to remove staff member from hat.', 'Error'));
            return { succeeded: false, error: (error as Error).message };
        }
    }

    /**
     * Finds all hats linked to a specific position (part).
     * A hat is linked to a position if the position's partKey is in the hat's positionArray.
     *
     * @param organization The organization object containing orgHats
     * @param partKey The part/position key to find hats for
     * @returns Array of OrgHat objects linked to this position
     */
    public getHatsForPosition(organization: Organization, partKey: string): OrgHat[] {
        const hats: OrgHat[] = [];

        if (!organization?.orgHats?.hatsKeys) {
            return hats;
        }

        // Also find the corresponding partKey/key from orgParts for cross-reference
        // Nested parts may have both a human-readable 'key' (e.g., 'unit-ic')
        // and a Firebase ID 'partKey' (e.g., '4jpEAzTIBGfU5HlrU4EY')
        const alternateKeys = this.findAlternateKeysForPosition(organization, partKey);
        const allKeysToMatch = [partKey, ...alternateKeys];

        const hatKeys = organization.orgHats.hatsKeys;

        for (const hatKey of hatKeys) {
            const hat = organization.orgHats[hatKey] as OrgHat;
            if (!hat) continue;
            // Skip soft-deleted hats
            if ((hat as any).isSoftDeleted) continue;

            // Check multiple possible field names for position arrays
            // The hat can have positions as array of objects OR positionArray as array of strings
            let positionKeys: string[] = [];

            // Check positionArray (array of part keys as strings)
            if (Array.isArray((hat as any).positionArray)) {
                positionKeys = (hat as any).positionArray;
            }
            // Check positions (array of Position objects with key or partKey)
            else if (Array.isArray(hat.positions)) {
                positionKeys = hat.positions.map((p) => p.key || (p as any).partKey).filter(Boolean);
            }

            // Check if any of the hat's position keys match any of our keys to match
            const matches = positionKeys.some((pk) => allKeysToMatch.includes(pk));

            if (matches) {
                hats.push(hat);
            }
        }

        return hats;
    }

    /**
     * Finds all Special Rights hats in the organization.
     * Special Rights hats are org-wide and not linked to any position.
     */
    public getSpecialRightsHats(organization: Organization): OrgHat[] {
        if (!organization?.orgHats?.hatsKeys) {
            return [];
        }

        const hats: OrgHat[] = [];
        for (const hatKey of organization.orgHats.hatsKeys) {
            const hat = organization.orgHats[hatKey] as OrgHat;
            if (!hat) continue;
            if ((hat as any).isSoftDeleted) continue;
            if ((hat as any).isSpecialRights) {
                hats.push(hat);
            }
        }

        return hats;
    }

    /**
     * Finds alternate keys for a position.
     * Nested parts can have both a human-readable 'key' (e.g., 'unit-ic')
     * and a Firebase ID 'partKey' (e.g., '4jpEAzTIBGfU5HlrU4EY').
     * This method searches through orgParts to find the corresponding alternate key.
     */
    private findAlternateKeysForPosition(organization: Organization, searchKey: string): string[] {
        const alternateKeys: string[] = [];
        const orgParts = organization?.orgParts || [];

        for (const orgPart of orgParts) {
            // Cast to any to access dynamic nested properties (sections, units, posts)
            const part = orgPart as any;
            // Check the department itself
            if (part.key === searchKey && part.partKey && part.partKey !== searchKey) {
                alternateKeys.push(part.partKey);
            }
            if (part.partKey === searchKey && part.key && part.key !== searchKey) {
                alternateKeys.push(part.key);
            }

            // Check nested sections
            if (part.sections && Array.isArray(part.sections)) {
                for (const section of part.sections) {
                    if (section.key === searchKey && section.partKey && section.partKey !== searchKey) {
                        alternateKeys.push(section.partKey);
                    }
                    if (section.partKey === searchKey && section.key && section.key !== searchKey) {
                        alternateKeys.push(section.key);
                    }

                    // Check units within sections
                    if (section.units && Array.isArray(section.units)) {
                        for (const unit of section.units) {
                            if (unit.key === searchKey && unit.partKey && unit.partKey !== searchKey) {
                                alternateKeys.push(unit.partKey);
                            }
                            if (unit.partKey === searchKey && unit.key && unit.key !== searchKey) {
                                alternateKeys.push(unit.key);
                            }

                            // Check posts within units
                            if (unit.posts && Array.isArray(unit.posts)) {
                                for (const post of unit.posts) {
                                    if (post.key === searchKey && post.partKey && post.partKey !== searchKey) {
                                        alternateKeys.push(post.partKey);
                                    }
                                    if (post.partKey === searchKey && post.key && post.key !== searchKey) {
                                        alternateKeys.push(post.key);
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Check units directly under department
            if (part.units && Array.isArray(part.units)) {
                for (const unit of part.units) {
                    if (unit.key === searchKey && unit.partKey && unit.partKey !== searchKey) {
                        alternateKeys.push(unit.partKey);
                    }
                    if (unit.partKey === searchKey && unit.key && unit.key !== searchKey) {
                        alternateKeys.push(unit.key);
                    }

                    // Check posts within units
                    if (unit.posts && Array.isArray(unit.posts)) {
                        for (const post of unit.posts) {
                            if (post.key === searchKey && post.partKey && post.partKey !== searchKey) {
                                alternateKeys.push(post.partKey);
                            }
                            if (post.partKey === searchKey && post.key && post.key !== searchKey) {
                                alternateKeys.push(post.key);
                            }
                        }
                    }
                }
            }

            // Check posts directly under department
            if (part.posts && Array.isArray(part.posts)) {
                for (const post of part.posts) {
                    if (post.key === searchKey && post.partKey && post.partKey !== searchKey) {
                        alternateKeys.push(post.partKey);
                    }
                    if (post.partKey === searchKey && post.key && post.key !== searchKey) {
                        alternateKeys.push(post.key);
                    }
                }
            }
        }

        return alternateKeys;
    }

    /**
     * Checks if a user is already assigned to a specific hat.
     *
     * @param hat The hat to check
     * @param userId The user's uid to check
     * @returns true if user is already in the hat's userKeys
     */
    public isUserInHat(hat: OrgHat, userId: string): boolean {
        // Only check assignedToArray (active assignments)
        // Pending users should still be selectable for re-assignment
        const assignedUsers = hat.assignedToArray || [];
        return assignedUsers.includes(userId);
    }

    public isUserPendingInHat(hat: OrgHat, userId: string): boolean {
        const pendingUsers = hat.pendingAssignedToArray || [];
        return pendingUsers.includes(userId);
    }

    /**
     * Gets the display name for a hat, with fallback if name is missing.
     *
     * @param hat The hat to get the name for
     * @returns The hat's display name
     */
    public getHatDisplayName(hat: OrgHat): string {
        return hat.name || hat.key || 'Unnamed Hat';
    }

    // =========================================================================
    // APPROVAL WORKFLOW METHODS
    // =========================================================================

    /**
     * Assigns a user to a position with hat selection.
     * If the organization requires approval, writes to `pendingAssignedToArray` and
     * creates a `PendingPositionApproval` record. Otherwise, writes directly to
     * `assignedToArray` (existing behavior).
     *
     * @param orgId Organization ID
     * @param positionKey Position the user was dropped on (empty string for pure special-rights)
     * @param positionName Denormalized name for display
     * @param hatKeys All hat keys selected during assignment
     * @param userId User UID to assign
     * @param requireApproval Whether the organization requires approval
     * @param specialRightsHatKeys Subset of hatKeys that are positionless
     * @returns Result with succeeded, pending flag, and optional approvalId
     */
    public async assignUserToPosition(
        orgId: string,
        positionKey: string,
        positionName: string,
        hatKeys: string[],
        userId: string,
        requireApproval: boolean,
        specialRightsHatKeys: string[] = []
    ): Promise<{ succeeded: boolean; pending: boolean; approvalId?: string; error?: string }> {
        try {
            const user = await this.auth.currentUser;
            if (!user) {
                return { succeeded: false, pending: false, error: 'User not authenticated' };
            }

            // Allow empty hatKeys only when special rights hats are supplied —
            // supports the "modify existing user" flow where they already wear
            // every position hat on this post and we're only adding org-wide
            // special rights.
            if (!hatKeys.length && !specialRightsHatKeys.length) {
                return { succeeded: false, pending: false, error: 'No hats selected' };
            }

            const batch = this.db.firestore.batch();
            const now = Timestamp.now();

            if (requireApproval) {
                // Write to pendingAssignedToArray on each hat subcollection doc
                // AND sync to denormalized orgHats on the organization document
                const orgRef = this.db.firestore.collection('organizations').doc(orgId);
                const orgHatsUpdate: Record<string, any> = {};

                for (const hatKey of hatKeys) {
                    const hatRef = this.db.firestore
                        .collection('organizations')
                        .doc(orgId)
                        .collection('hats')
                        .doc(hatKey);
                    batch.update(hatRef, {
                        pendingAssignedToArray: arrayUnion(userId),
                        modifiedBy: user.uid,
                        modifiedOn: now,
                    });

                    // Also update the denormalized copy on org document
                    orgHatsUpdate[`orgHats.${hatKey}.pendingAssignedToArray`] = arrayUnion(userId);
                }

                // Also add special rights hats to pendingAssignedToArray
                for (const srHatKey of specialRightsHatKeys) {
                    const srHatRef = this.db.firestore
                        .collection('organizations')
                        .doc(orgId)
                        .collection('hats')
                        .doc(srHatKey);
                    batch.update(srHatRef, {
                        pendingAssignedToArray: arrayUnion(userId),
                        modifiedBy: user.uid,
                        modifiedOn: now,
                    });
                    orgHatsUpdate[`orgHats.${srHatKey}.pendingAssignedToArray`] = arrayUnion(userId);
                }

                // Batch update the org document with all hat changes
                batch.update(orgRef, orgHatsUpdate);

                // Create the approval record
                const approvalRef = this.db.firestore
                    .collection('organizations')
                    .doc(orgId)
                    .collection('pendingApprovals')
                    .doc();

                const approvalData: Record<string, any> = {
                    key: approvalRef.id,
                    uid: userId,
                    positionKey,
                    positionName,
                    hatKeys,
                    orgId,
                    requestedBy: user.uid,
                    requestedOn: now as any,
                    status: 'pending',
                    createdBy: user.uid,
                    createdOn: now as any,
                    modifiedBy: user.uid,
                    modifiedOn: now as any,
                };

                if (specialRightsHatKeys.length) {
                    approvalData['specialRightsHatKeys'] = specialRightsHatKeys;
                }

                batch.set(approvalRef, approvalData);

                await batch.commit();

                this.ngZone.run(() =>
                    this.toastr.info(
                        `Staff member assigned to ${positionName || 'org part'} \u2014 pending approval`,
                        'Pending Approval'
                    )
                );

                return { succeeded: true, pending: true, approvalId: approvalRef.id };
            } else {
                // No approval needed — write directly to assignedToArray (existing behavior)
                const orgRef = this.db.firestore.collection('organizations').doc(orgId);
                const orgHatsUpdate: Record<string, any> = {};

                for (const hatKey of hatKeys) {
                    const hatRef = this.db.firestore
                        .collection('organizations')
                        .doc(orgId)
                        .collection('hats')
                        .doc(hatKey);
                    batch.update(hatRef, {
                        assignedToArray: arrayUnion(userId),
                        userKeys: arrayUnion(userId),
                        modifiedBy: user.uid,
                        modifiedOn: now,
                    });

                    // Also update the denormalized copy on org document
                    orgHatsUpdate[`orgHats.${hatKey}.assignedToArray`] = arrayUnion(userId);
                    orgHatsUpdate[`orgHats.${hatKey}.userKeys`] = arrayUnion(userId);
                }

                // Batch update the org document with all hat changes
                batch.update(orgRef, orgHatsUpdate);

                await batch.commit();

                this.ngZone.run(() =>
                    this.toastr.success('Staff member assignment initiated. Security task created.', 'Hat Assignment')
                );

                return { succeeded: true, pending: false };
            }
        } catch (error) {
            console.error('[OrgHattingService] Error in assignUserToPosition:', error);
            this.ngZone.run(() => this.toastr.error('Failed to assign staff member to org part.', 'Error'));
            return { succeeded: false, pending: false, error: (error as Error).message };
        }
    }

    /**
     * Routes newly added users through the approval workflow during a hat edit.
     * Writes new users to `pendingAssignedToArray` (not `assignedToArray`) and creates
     * a `PendingPositionApproval` record with `source: 'hat-edit'`.
     *
     * @param orgId Organization ID
     * @param hatKey The hat being edited
     * @param newUserIds UIDs that were added (not previously in assignedToArray or pendingAssignedToArray)
     * @param positionKey Optional position key linked to the hat (null if org-wide)
     * @param positionName Optional resolved position name for display
     * @returns Result with succeeded flag and count of users routed to pending
     */
    public async addUsersPendingApproval(
        orgId: string,
        hatKey: string,
        newUserIds: string[],
        positionKey: string | null,
        positionName: string | null,
        skipHatDocUpdate = false
    ): Promise<{ succeeded: boolean; pendingCount: number; error?: string }> {
        try {
            const user = await this.auth.currentUser;
            if (!user) {
                return { succeeded: false, pendingCount: 0, error: 'User not authenticated' };
            }

            if (!newUserIds.length) {
                return { succeeded: true, pendingCount: 0 };
            }

            const batch = this.db.firestore.batch();
            const now = Timestamp.now();

            // Write each new user to pendingAssignedToArray on the hat doc (unless caller already did it)
            if (!skipHatDocUpdate) {
                const hatRef = this.db.firestore.collection('organizations').doc(orgId).collection('hats').doc(hatKey);

                for (const userId of newUserIds) {
                    batch.update(hatRef, {
                        pendingAssignedToArray: arrayUnion(userId),
                        modifiedBy: user.uid,
                        modifiedOn: now,
                    });
                }
            }

            // Also update denormalized copy on org document
            const orgRef = this.db.firestore.collection('organizations').doc(orgId);
            const orgHatsUpdate: Record<string, any> = {};
            for (const userId of newUserIds) {
                orgHatsUpdate[`orgHats.${hatKey}.pendingAssignedToArray`] = arrayUnion(...newUserIds);
            }
            batch.update(orgRef, orgHatsUpdate);

            // Create one approval record per new user
            for (const userId of newUserIds) {
                const approvalRef = this.db.firestore
                    .collection('organizations')
                    .doc(orgId)
                    .collection('pendingApprovals')
                    .doc();

                const approvalData: Record<string, any> = {
                    key: approvalRef.id,
                    uid: userId,
                    positionKey: positionKey || '',
                    hatKeys: [hatKey],
                    orgId,
                    source: 'hat-edit',
                    requestedBy: user.uid,
                    requestedOn: now as any,
                    status: 'pending',
                    createdBy: user.uid,
                    createdOn: now as any,
                    modifiedBy: user.uid,
                    modifiedOn: now as any,
                };

                if (positionName) {
                    approvalData['positionName'] = positionName;
                }

                batch.set(approvalRef, approvalData);
            }

            await batch.commit();

            const plural = newUserIds.length > 1 ? 's' : '';
            this.ngZone.run(() =>
                this.toastr.info(
                    `${newUserIds.length} staff member${plural} added \u2014 pending approval`,
                    'Pending Approval'
                )
            );

            return { succeeded: true, pendingCount: newUserIds.length };
        } catch (error) {
            console.error('[OrgHattingService] Error in addUsersPendingApproval:', error);
            this.ngZone.run(() => this.toastr.error('Failed to create pending approval.', 'Error'));
            return { succeeded: false, pendingCount: 0, error: (error as Error).message };
        }
    }

    /**
     * Removes users from `pendingAssignedToArray` on a hat (and denormalized org doc).
     * Also marks any matching `PendingPositionApproval` records as rejected.
     * Used when an admin unticks a pending user in the Edit Hat dialog.
     *
     * @param orgId Organization ID
     * @param hatKey The hat being edited
     * @param userIds UIDs to remove from pending
     * @returns Result with succeeded flag
     */
    public async removeUsersPendingApproval(
        orgId: string,
        hatKey: string,
        userIds: string[],
        skipHatDocUpdate = false
    ): Promise<{ succeeded: boolean; error?: string }> {
        try {
            const user = await this.auth.currentUser;
            if (!user) {
                return { succeeded: false, error: 'User not authenticated' };
            }

            if (!userIds.length) {
                return { succeeded: true };
            }

            const batch = this.db.firestore.batch();
            const now = Timestamp.now();

            // Remove each user from pendingAssignedToArray on the hat doc (unless caller already did it)
            if (!skipHatDocUpdate) {
                const hatRef = this.db.firestore.collection('organizations').doc(orgId).collection('hats').doc(hatKey);

                for (const userId of userIds) {
                    batch.update(hatRef, {
                        pendingAssignedToArray: arrayRemove(userId),
                        modifiedBy: user.uid,
                        modifiedOn: now,
                    });
                }
            }

            // Also update denormalized copy on org document
            const orgRef = this.db.firestore.collection('organizations').doc(orgId);
            for (const userId of userIds) {
                batch.update(orgRef, {
                    [`orgHats.${hatKey}.pendingAssignedToArray`]: arrayRemove(userId),
                });
            }

            await batch.commit();

            // Best-effort: find and reject matching approval records
            for (const userId of userIds) {
                try {
                    const approvalSnap = await this.db.firestore
                        .collection('organizations')
                        .doc(orgId)
                        .collection('pendingApprovals')
                        .where('uid', '==', userId)
                        .where('status', '==', 'pending')
                        .get();

                    for (const doc of approvalSnap.docs) {
                        const data = doc.data();
                        if (data['hatKeys']?.includes(hatKey)) {
                            await doc.ref.update({
                                status: 'rejected',
                                rejectedBy: user.uid,
                                rejectedOn: now,
                                rejectionReason: 'Removed during hat edit',
                                modifiedBy: user.uid,
                                modifiedOn: now,
                            });
                            await this.archiveAndDeleteApproval(orgId, doc.id);
                        }
                    }
                } catch (err) {
                    // Non-critical — pending array is already updated
                    console.warn('[OrgHattingService] Could not update approval record:', err);
                }
            }

            const plural = userIds.length > 1 ? 's' : '';
            this.ngZone.run(() =>
                this.toastr.info(`${userIds.length} pending staff member${plural} removed`, 'Pending Removed')
            );

            return { succeeded: true };
        } catch (error) {
            console.error('[OrgHattingService] Error in removeUsersPendingApproval:', error);
            this.ngZone.run(() => this.toastr.error('Failed to remove pending approval.', 'Error'));
            return { succeeded: false, error: (error as Error).message };
        }
    }

    /**
     * Add additional hats to a user's ALREADY-EXISTING pending approval.
     *
     * Used by the "modify" flow in the Bulk Assign Staff dialog when the
     * target user is currently in `pendingAssignedToArray` (not yet approved).
     * Rather than creating a duplicate approval record, we:
     *   - arrayUnion the new hat keys onto the existing approval doc's
     *     `hatKeys` / `specialRightsHatKeys` fields, so when an approver
     *     acts on that approval they see the full set the user should
     *     receive.
     *   - arrayUnion the user onto each new hat's `pendingAssignedToArray`
     *     (and the denormalized `orgHats.{key}.pendingAssignedToArray`).
     *
     * All writes use arrayUnion — safe to call repeatedly. Hats the user
     * already has pending are unaffected.
     *
     * This method ignores the org's current requireApproval setting —
     * the user is already pending, so new hats go to the pending side.
     */
    public async addHatsToPendingAssignment(
        orgId: string,
        approvalId: string | undefined,
        userId: string,
        hatKeys: string[],
        specialRightsHatKeys: string[] = []
    ): Promise<{ succeeded: boolean; pending: boolean; error?: string }> {
        try {
            const user = await this.auth.currentUser;
            if (!user) {
                return { succeeded: false, pending: false, error: 'User not authenticated' };
            }

            if (!hatKeys.length && !specialRightsHatKeys.length) {
                return { succeeded: false, pending: false, error: 'No hats selected' };
            }

            const batch = this.db.firestore.batch();
            const now = Timestamp.now();
            const orgRef = this.db.firestore.collection('organizations').doc(orgId);
            const orgHatsUpdate: Record<string, any> = {};

            const allKeys = [...hatKeys, ...specialRightsHatKeys];
            for (const hatKey of allKeys) {
                const hatRef = this.db.firestore.collection('organizations').doc(orgId).collection('hats').doc(hatKey);
                batch.update(hatRef, {
                    pendingAssignedToArray: arrayUnion(userId),
                    modifiedBy: user.uid,
                    modifiedOn: now,
                });
                orgHatsUpdate[`orgHats.${hatKey}.pendingAssignedToArray`] = arrayUnion(userId);
            }
            batch.update(orgRef, orgHatsUpdate);

            // If there is an existing approval record, merge new hats into it
            // so the approver sees the full set in one go. When no approval
            // record exists (legacy pending state) we only update the hat
            // docs — the user's pending status remains and can still be
            // resolved via approvePendingUserDirectly / rejection flow.
            if (approvalId) {
                const approvalRef = this.db.firestore
                    .collection('organizations')
                    .doc(orgId)
                    .collection('pendingApprovals')
                    .doc(approvalId);
                const approvalUpdate: Record<string, any> = {
                    modifiedBy: user.uid,
                    modifiedOn: now as any,
                };
                if (hatKeys.length) {
                    approvalUpdate['hatKeys'] = arrayUnion(...hatKeys);
                }
                if (specialRightsHatKeys.length) {
                    approvalUpdate['specialRightsHatKeys'] = arrayUnion(...specialRightsHatKeys);
                }
                batch.update(approvalRef, approvalUpdate);
            }

            await batch.commit();

            this.ngZone.run(() =>
                this.toastr.info(
                    "Additional hats added to user's pending approval request.",
                    'Pending Approval Updated'
                )
            );

            return { succeeded: true, pending: true };
        } catch (error) {
            console.error('[OrgHattingService] Error in addHatsToPendingAssignment:', error);
            this.ngZone.run(() => this.toastr.error('Failed to add hats to pending assignment.', 'Error'));
            return { succeeded: false, pending: false, error: (error as Error).message };
        }
    }

    /**
     * Approves a pending position assignment.
     * Moves the user from `pendingAssignedToArray` to `assignedToArray` on approved hats.
     * Removes from `pendingAssignedToArray` on deselected hats.
     * Updates the approval record.
     *
     * @param orgId Organization ID
     * @param approvalId The PendingPositionApproval document key
     * @param approvedHatKeys Hats the approver actually approved (may differ from original)
     * @returns Result with succeeded flag
     */
    public async approvePositionAssignment(
        orgId: string,
        approvalId: string,
        approvedHatKeys: string[]
    ): Promise<{ succeeded: boolean; error?: string }> {
        try {
            const user = await this.auth.currentUser;
            if (!user) {
                return { succeeded: false, error: 'User not authenticated' };
            }

            // Read the approval record
            const approvalDoc = await this.db.firestore
                .collection('organizations')
                .doc(orgId)
                .collection('pendingApprovals')
                .doc(approvalId)
                .get();

            if (!approvalDoc.exists) {
                return { succeeded: false, error: 'Approval record not found' };
            }

            const approval = approvalDoc.data() as PendingPositionApproval;
            if (approval.status !== 'pending') {
                return { succeeded: false, error: `Approval already ${approval.status}` };
            }

            const batch = this.db.firestore.batch();
            const now = Timestamp.now();
            const userId = approval.uid;
            const originalHatKeys = approval.hatKeys;

            // For approved hats: move from pending to active
            const orgRef = this.db.firestore.collection('organizations').doc(orgId);
            const orgHatsUpdate: Record<string, any> = {};

            for (const hatKey of approvedHatKeys) {
                const hatRef = this.db.firestore.collection('organizations').doc(orgId).collection('hats').doc(hatKey);
                batch.update(hatRef, {
                    pendingAssignedToArray: arrayRemove(userId),
                    assignedToArray: arrayUnion(userId),
                    userKeys: arrayUnion(userId),
                    modifiedBy: user.uid,
                    modifiedOn: now,
                });
                // Sync denormalized org doc
                orgHatsUpdate[`orgHats.${hatKey}.pendingAssignedToArray`] = arrayRemove(userId);
                orgHatsUpdate[`orgHats.${hatKey}.assignedToArray`] = arrayUnion(userId);
                orgHatsUpdate[`orgHats.${hatKey}.userKeys`] = arrayUnion(userId);
            }

            // For deselected hats (in original but not in approved): remove from pending only
            const deselectedHatKeys = originalHatKeys.filter((k) => !approvedHatKeys.includes(k));
            for (const hatKey of deselectedHatKeys) {
                const hatRef = this.db.firestore.collection('organizations').doc(orgId).collection('hats').doc(hatKey);
                batch.update(hatRef, {
                    pendingAssignedToArray: arrayRemove(userId),
                    modifiedBy: user.uid,
                    modifiedOn: now,
                });
                orgHatsUpdate[`orgHats.${hatKey}.pendingAssignedToArray`] = arrayRemove(userId);
            }

            // Also approve special rights hats: move from pending to active
            const specialRightsHatKeys = approval.specialRightsHatKeys || [];
            for (const srHatKey of specialRightsHatKeys) {
                const srHatRef = this.db.firestore
                    .collection('organizations')
                    .doc(orgId)
                    .collection('hats')
                    .doc(srHatKey);
                batch.update(srHatRef, {
                    pendingAssignedToArray: arrayRemove(userId),
                    assignedToArray: arrayUnion(userId),
                    userKeys: arrayUnion(userId),
                    modifiedBy: user.uid,
                    modifiedOn: now,
                });
                orgHatsUpdate[`orgHats.${srHatKey}.pendingAssignedToArray`] = arrayRemove(userId);
                orgHatsUpdate[`orgHats.${srHatKey}.assignedToArray`] = arrayUnion(userId);
                orgHatsUpdate[`orgHats.${srHatKey}.userKeys`] = arrayUnion(userId);
            }

            // Batch update org document with denormalized changes
            if (Object.keys(orgHatsUpdate).length > 0) {
                batch.update(orgRef, orgHatsUpdate);
            }

            // Update approval record
            const approvalRef = this.db.firestore
                .collection('organizations')
                .doc(orgId)
                .collection('pendingApprovals')
                .doc(approvalId);
            batch.update(approvalRef, {
                status: 'approved',
                approvedHatKeys,
                approvedBy: user.uid,
                approvedOn: now,
                modifiedBy: user.uid,
                modifiedOn: now,
            });

            await batch.commit();

            // Archive processed record then delete from active collection
            await this.archiveAndDeleteApproval(orgId, approvalId);

            this.ngZone.run(() =>
                this.toastr.success(
                    `Approved ${approvedHatKeys.length} hat(s). Security tasks processing.`,
                    'Assignment Approved'
                )
            );

            return { succeeded: true };
        } catch (error) {
            console.error('[OrgHattingService] Error approving position assignment:', error);
            this.ngZone.run(() => this.toastr.error('Failed to approve assignment.', 'Error'));
            return { succeeded: false, error: (error as Error).message };
        }
    }

    /**
     * Rejects a pending position assignment.
     * Removes the user from `pendingAssignedToArray` on ALL hats in the approval record.
     * Updates the approval record with rejection status and optional reason.
     *
     * @param orgId Organization ID
     * @param approvalId The PendingPositionApproval document key
     * @param reason Optional rejection reason
     * @returns Result with succeeded flag
     */
    public async rejectPositionAssignment(
        orgId: string,
        approvalId: string,
        reason?: string
    ): Promise<{ succeeded: boolean; error?: string }> {
        try {
            const user = await this.auth.currentUser;
            if (!user) {
                return { succeeded: false, error: 'User not authenticated' };
            }

            // Read the approval record
            const approvalDoc = await this.db.firestore
                .collection('organizations')
                .doc(orgId)
                .collection('pendingApprovals')
                .doc(approvalId)
                .get();

            if (!approvalDoc.exists) {
                return { succeeded: false, error: 'Approval record not found' };
            }

            const approval = approvalDoc.data() as PendingPositionApproval;
            if (approval.status !== 'pending') {
                return { succeeded: false, error: `Approval already ${approval.status}` };
            }

            const batch = this.db.firestore.batch();
            const now = Timestamp.now();
            const userId = approval.uid;

            // Remove user from pendingAssignedToArray on ALL hats
            const orgRef = this.db.firestore.collection('organizations').doc(orgId);
            const orgHatsUpdate: Record<string, any> = {};

            for (const hatKey of approval.hatKeys) {
                const hatRef = this.db.firestore.collection('organizations').doc(orgId).collection('hats').doc(hatKey);
                batch.update(hatRef, {
                    pendingAssignedToArray: arrayRemove(userId),
                    modifiedBy: user.uid,
                    modifiedOn: now,
                });
                // Sync denormalized org doc
                orgHatsUpdate[`orgHats.${hatKey}.pendingAssignedToArray`] = arrayRemove(userId);
            }

            // Also remove special rights hats from pendingAssignedToArray
            const specialRightsHatKeys = approval.specialRightsHatKeys || [];
            for (const srHatKey of specialRightsHatKeys) {
                const srHatRef = this.db.firestore
                    .collection('organizations')
                    .doc(orgId)
                    .collection('hats')
                    .doc(srHatKey);
                batch.update(srHatRef, {
                    pendingAssignedToArray: arrayRemove(userId),
                    modifiedBy: user.uid,
                    modifiedOn: now,
                });
                orgHatsUpdate[`orgHats.${srHatKey}.pendingAssignedToArray`] = arrayRemove(userId);
            }

            // Batch update org document
            if (Object.keys(orgHatsUpdate).length > 0) {
                batch.update(orgRef, orgHatsUpdate);
            }

            // Update approval record
            const approvalRef = this.db.firestore
                .collection('organizations')
                .doc(orgId)
                .collection('pendingApprovals')
                .doc(approvalId);
            batch.update(approvalRef, {
                status: 'rejected',
                rejectedBy: user.uid,
                rejectedOn: now,
                rejectionReason: reason || null,
                modifiedBy: user.uid,
                modifiedOn: now,
            });

            await batch.commit();

            // Archive processed record then delete from active collection
            await this.archiveAndDeleteApproval(orgId, approvalId);

            this.ngZone.run(() =>
                this.toastr.warning('Assignment rejected. Staff member removed from pending.', 'Assignment Rejected')
            );

            return { succeeded: true };
        } catch (error) {
            console.error('[OrgHattingService] Error rejecting position assignment:', error);
            this.ngZone.run(() => this.toastr.error('Failed to reject assignment.', 'Error'));
            return { succeeded: false, error: (error as Error).message };
        }
    }

    /**
     * Removes a pending user from pendingAssignedToArray on the given hats
     * and the org document's denormalized copy.
     * Also cleans up any matching pendingApprovals records.
     */
    public async removePendingUserFromHats(orgId: string, hats: OrgHat[], userId: string): Promise<void> {
        const batch = this.db.firestore.batch();
        const orgRef = this.db.firestore.collection('organizations').doc(orgId);
        const orgHatsUpdate: Record<string, any> = {};

        for (const hat of hats) {
            const hatKey = hat.key;
            const hatRef = this.db.firestore.collection('organizations').doc(orgId).collection('hats').doc(hatKey);
            batch.update(hatRef, {
                pendingAssignedToArray: arrayRemove(userId),
            });
            orgHatsUpdate[`orgHats.${hatKey}.pendingAssignedToArray`] = arrayRemove(userId);
        }

        if (Object.keys(orgHatsUpdate).length > 0) {
            batch.update(orgRef, orgHatsUpdate);
        }

        await batch.commit();

        // Best-effort: clean up any matching pendingApprovals records
        const hatKeys = hats.map((h) => h.key);
        for (const hatKey of hatKeys) {
            await this.cleanupPendingApprovalRecords(
                orgId,
                hatKey,
                userId,
                'rejected',
                'Pending user removed from hat'
            );
        }
    }

    /**
     * Directly approves a pending user by moving them from pendingAssignedToArray
     * to assignedToArray on the given hats. Used when no approval record exists.
     */
    public async approvePendingUserDirectly(
        orgId: string,
        hats: OrgHat[],
        userId: string
    ): Promise<{ succeeded: boolean; error?: string }> {
        try {
            const currentUser = await this.auth.currentUser;
            if (!currentUser) {
                return { succeeded: false, error: 'User not authenticated' };
            }

            // Phase B.3 gate (spec §4.3, §6.7 Option α): refuse approval while the
            // user still has an outstanding invitation for this org. Approval can
            // only proceed once they accept and their auth uid is linked.
            const pendingInv = await this.db.firestore
                .collection('invitations')
                .where('orgKey', '==', orgId)
                .where('targetUid', '==', userId)
                .where('status', '==', 'pending')
                .limit(1)
                .get();
            if (!pendingInv.empty) {
                return {
                    succeeded: false,
                    error: 'User has not yet accepted the invitation. Approval is unavailable until they accept.',
                };
            }

            const batch = this.db.firestore.batch();
            const orgRef = this.db.firestore.collection('organizations').doc(orgId);
            const orgHatsUpdate: Record<string, any> = {};
            const now = Timestamp.now();

            for (const hat of hats) {
                const hatKey = hat.key;
                const hatRef = this.db.firestore.collection('organizations').doc(orgId).collection('hats').doc(hatKey);

                batch.update(hatRef, {
                    pendingAssignedToArray: arrayRemove(userId),
                    assignedToArray: arrayUnion(userId),
                    userKeys: arrayUnion(userId),
                    modifiedBy: currentUser.uid,
                    modifiedOn: now,
                });

                orgHatsUpdate[`orgHats.${hatKey}.pendingAssignedToArray`] = arrayRemove(userId);
                orgHatsUpdate[`orgHats.${hatKey}.assignedToArray`] = arrayUnion(userId);
                orgHatsUpdate[`orgHats.${hatKey}.userKeys`] = arrayUnion(userId);
            }

            if (Object.keys(orgHatsUpdate).length > 0) {
                batch.update(orgRef, orgHatsUpdate);
            }

            await batch.commit();

            // Best-effort: clean up any matching pendingApprovals records
            const hatKeys = hats.map((h) => h.key);
            for (const hatKey of hatKeys) {
                await this.cleanupPendingApprovalRecords(
                    orgId,
                    hatKey,
                    userId,
                    'approved',
                    'Directly approved without approval record'
                );
            }

            return { succeeded: true };
        } catch (error) {
            console.error('[OrgHattingService] Error directly approving pending user:', error);
            return { succeeded: false, error: (error as Error).message };
        }
    }

    /**
     * Loads all pending approval records for an organization.
     * Used by the approval UI to show outstanding approvals.
     *
     * @param orgId Organization ID
     * @param statusFilter Filter by status (default: 'pending')
     * @returns Array of PendingPositionApproval records
     */
    public async getPendingApprovals(
        orgId: string,
        statusFilter: 'pending' | 'approved' | 'rejected' = 'pending'
    ): Promise<PendingPositionApproval[]> {
        try {
            const snapshot = await this.db.firestore
                .collection('organizations')
                .doc(orgId)
                .collection('pendingApprovals')
                .where('status', '==', statusFilter)
                .orderBy('requestedOn', 'desc')
                .get();

            // Stamp `key`/`orgId` from the query context — wizard/invitation-written
            // records historically lack `orgId` (see loadPendingApprovalsForUser).
            return snapshot.docs.map((doc) => ({ ...doc.data(), key: doc.id, orgId }) as PendingPositionApproval);
        } catch (error) {
            console.error('[OrgHattingService] Error loading pending approvals:', error);
            return [];
        }
    }

    /**
     * Phase B.3 — returns the set of user uids in this org that still have an
     * outstanding (status='pending') invitation. Templates use this to render
     * dashed "Pending Acceptance" pills vs solid "Pending Approval" pills and
     * to gate the Approve action. Spec §5.3 + §6.7 Option α (queries by
     * `targetUid`, written by staff-stage backend B.1).
     */
    public async getPendingInvitationUidsForOrg(orgId: string): Promise<Set<string>> {
        try {
            const snap = await this.db.firestore
                .collection('invitations')
                .where('orgKey', '==', orgId)
                .where('status', '==', 'pending')
                .get();
            const uids = new Set<string>();
            snap.docs.forEach((doc) => {
                const data = doc.data() as { targetUid?: string };
                if (data.targetUid) uids.add(data.targetUid);
            });
            return uids;
        } catch (error) {
            console.error('[OrgHattingService] Error loading pending invitation uids:', error);
            return new Set<string>();
        }
    }

    /**
     * Loads pending approval records for a specific user on a specific position.
     */
    public async loadPendingApprovalsForUser(
        orgId: string,
        userId: string,
        positionKey?: string
    ): Promise<Array<PendingPositionApproval & { id: string }>> {
        try {
            let query = this.db.firestore
                .collection('organizations')
                .doc(orgId)
                .collection('pendingApprovals')
                .where('uid', '==', userId)
                .where('status', '==', 'pending');

            if (positionKey) {
                query = query.where('positionKey', '==', positionKey);
            }

            const snapshot = await query.get();
            // Stamp `key`/`orgId` from the query context — records written by the
            // wizard staff stage / invitation acceptance historically lack `orgId`,
            // and the approve/reject flows locate the doc through those fields.
            return snapshot.docs.map(
                (doc) => ({ ...doc.data(), id: doc.id, key: doc.id, orgId }) as PendingPositionApproval & { id: string }
            );
        } catch (error) {
            console.error('[OrgHattingService] Error loading pending approvals for user:', error);
            return [];
        }
    }

    /**
     * Checks if a user has any pending hat assignments in the organization.
     * Uses the loaded organization data (no extra queries).
     */
    public isUserPendingOnAnyHat(organization: Organization, userId: string): boolean {
        if (!organization?.orgHats?.hatsKeys) return false;
        for (const hatKey of organization.orgHats.hatsKeys) {
            const hat = organization.orgHats[hatKey] as OrgHat;
            if (!hat || hat.isSoftDeleted) continue;
            if (hat.pendingAssignedToArray?.includes(userId)) return true;
        }
        return false;
    }

    /**
     * Best-effort cleanup of pendingApprovals records for a specific user+hat combination.
     * Finds all pending records where the user and hat match, and updates their status.
     * Wrapped in try/catch so failures don't break the calling operation.
     */
    /**
     * Archives a processed pendingApprovals record to pendingApprovalsHistory,
     * then deletes it from the active pendingApprovals collection.
     * Preserves the same doc ID for traceability.
     */
    private async archiveAndDeleteApproval(orgId: string, approvalId: string): Promise<void> {
        try {
            const sourceRef = this.db.firestore
                .collection('organizations')
                .doc(orgId)
                .collection('pendingApprovals')
                .doc(approvalId);
            const snap = await sourceRef.get();
            if (!snap.exists) return;

            const data = snap.data()!;
            const archiveRef = this.db.firestore
                .collection('organizations')
                .doc(orgId)
                .collection('pendingApprovalsHistory')
                .doc(approvalId);

            await archiveRef.set({ ...data, archivedOn: Timestamp.now() });
            await sourceRef.delete();
        } catch (err) {
            console.warn('[OrgHattingService] archiveAndDeleteApproval failed (non-critical):', err);
        }
    }

    private async cleanupPendingApprovalRecords(
        orgId: string,
        hatKey: string,
        userId: string,
        newStatus: 'approved' | 'rejected' | 'cancelled',
        reason: string
    ): Promise<void> {
        try {
            const currentUser = await this.auth.currentUser;
            const snapshot = await this.db.firestore
                .collection('organizations')
                .doc(orgId)
                .collection('pendingApprovals')
                .where('uid', '==', userId)
                .where('status', '==', 'pending')
                .get();

            for (const doc of snapshot.docs) {
                const data = doc.data();
                const hatKeysArr: string[] = data['hatKeys'] || [];
                if (hatKeysArr.includes(hatKey)) {
                    const updateData: Record<string, any> = {
                        status: newStatus,
                        [`${newStatus}By`]: currentUser?.uid || 'system',
                        [`${newStatus}On`]: Timestamp.now(),
                    };
                    if (newStatus === 'rejected') {
                        updateData['rejectionReason'] = reason;
                    }
                    await doc.ref.update(updateData);
                    await this.archiveAndDeleteApproval(orgId, doc.id);
                }
            }
        } catch (err) {
            console.warn('[OrgHattingService] Best-effort pendingApprovals cleanup failed:', err);
        }
    }
}
